import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import usePermissions from '../../hooks/usePermissions';
import { groupedThemes } from '../../themes';
import './ThemePicker.css';

/**
 * Temaväljaren i profilens utseendeinställningar.
 *
 * Ersatte en ljust/mörkt-kryssruta när temana blev fler än två. Varje tema
 * visas som ett kort med tre färgprickar hämtade ur manifestet, så man ser
 * vad man väljer utan att prova sig fram.
 *
 * Mörkt och ljust är gratis; övriga teman köps i tipsbutiken. Teman spelaren
 * inte äger visas ändå — med lås och länk till butiken. Det är hela poängen
 * med att visa dem: förhandsvisningen bär `data-theme` och läser temats
 * riktiga tokens, så man ser exakt vad man får innan man betalar.
 *
 * Temanamnen översätts INTE — Mirage heter Mirage på alla språk. Bara
 * gruppetiketterna går genom i18n.
 *
 * @param {object} props
 * @param {string} props.value - valt tema-id
 * @param {string[]} props.owned - tema-id spelaren får använda (från servern)
 * @param {(themeId: string) => void} props.onChange - anropas med nytt id,
 *   eller tom sträng för "följ communityn"
 * @param {boolean} props.followsCommunity - följer spelaren communityns tema
 * @param {string} props.communityTheme - communityns tema, för färgprickarna
 * @returns {JSX.Element}
 */
export default function ThemePicker({
  value, owned, onChange, followsCommunity, communityTheme,
}) {
  const { t } = useTranslation();
  const { features } = usePermissions();
  const groups = groupedThemes();
  const ownedSet = new Set(owned || []);
  // Butiksrutten är gated på SHOP_ENABLED i App.js. Är butiken av finns ingen
  // sida att länka till — låset visas ändå, men som text i stället för en
  // länk som leder till 404.
  const shopOpen = features?.SHOP_ENABLED !== false;

  return (
    <div className="theme-picker">
      {groups.map(({ group, themes }) => (
        <fieldset className="theme-picker-group" key={group}>
          <legend className="theme-picker-group-label">
            {t(`settings.themeGroup.${group}`)}
          </legend>

          <div className="theme-picker-grid">
            {/* Följ communityn ligger först i basgruppen, inte som ett tema
                bland andra: det är ett val OM teman, inte ett tema. Servern
                sparar det som tomt fält och resolvar vid läsning, så den som
                väljer det byter utseende när admin gör det. */}
            {group === 'base' && (
              <label
                className={[
                  'theme-picker-option',
                  followsCommunity ? 'theme-picker-option--active' : '',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="theme"
                  className="theme-picker-radio"
                  value=""
                  checked={followsCommunity}
                  onChange={() => onChange('')}
                />
                <span className="theme-picker-reset" data-theme="dark">
                  <span
                    className="theme-picker-swatch"
                    data-theme={communityTheme}
                    aria-hidden="true"
                  >
                    <span className="theme-picker-chip theme-picker-chip--bg" />
                    <span className="theme-picker-chip theme-picker-chip--accent" />
                    <span className="theme-picker-chip theme-picker-chip--alt" />
                  </span>
                </span>
                <span className="theme-picker-name">
                  {t('settings.themeFollowCommunity')}
                </span>
              </label>
            )}

            {themes.map((theme) => {
              const locked = !ownedSet.has(theme.id);
              const classes = ['theme-picker-option'];
              if (!followsCommunity && theme.id === value) {
                classes.push('theme-picker-option--active');
              }
              if (locked) classes.push('theme-picker-option--locked');

              return (
                <label className={classes.join(' ')} key={theme.id}>
                  <input
                    type="radio"
                    name="theme"
                    className="theme-picker-radio"
                    value={theme.id}
                    checked={!followsCommunity && theme.id === value}
                    disabled={locked}
                    onChange={() => onChange(theme.id)}
                  />

                  {/* data-theme på förhandsvisningen aktiverar temats eget
                      variabelblock på just det elementet, så prickarna läser
                      riktiga tokens i stället för kopierade hexvärden. Då kan
                      förhandsvisningen aldrig glida isär från paletten.

                      Yttre lagret nollställer: ett temablock sätter bara de
                      tokens temat deklarerar, och resten ärvs från det tema
                      sidan redan har. Prickarna här läser bara tokens varje
                      tema deklarerar, men lagret kostar ingenting och gör att
                      nästa preview-token som läggs till inte tyst blir fel.
                      Samma resonemang som i ThemePreview. */}
                  <span className="theme-picker-reset" data-theme="dark">
                    <span
                      className="theme-picker-swatch"
                      data-theme={theme.id}
                      aria-hidden="true"
                    >
                      <span className="theme-picker-chip theme-picker-chip--bg" />
                      <span className="theme-picker-chip theme-picker-chip--accent" />
                      <span className="theme-picker-chip theme-picker-chip--alt" />
                    </span>
                  </span>

                  <span className="theme-picker-name">{theme.label}</span>

                  {locked && (shopOpen ? (
                    <Link className="theme-picker-lock" to="/shop">
                      {t('settings.themeLocked')}
                    </Link>
                  ) : (
                    <span className="theme-picker-lock">
                      {t('settings.themeLocked')}
                    </span>
                  ))}
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
